import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { Spinner } from "@mandrac/ui";
import { LoginPage } from "./features/auth/LoginPage";
import { ResetPage } from "./features/auth/ResetPage";
import { AdminLayout } from "./features/admin/AdminLayout";
import { ADMIN_PAGES, type AdminPageId } from "./features/admin/access";
import { RequirePage } from "./features/admin/RequirePage";
import { useI18n } from "./lib/i18n";

function Loading() {
  const { t } = useI18n();
  return (
    <div className="p-6">
      <Spinner label={t("common.loading")} />
    </div>
  );
}
const lazyPage = (loader: () => Promise<{ default: React.ComponentType }>) => {
  const C = lazy(loader);
  return (
    <Suspense fallback={<Loading />}>
      <C />
    </Suspense>
  );
};

const ADMIN_LOADERS: Record<AdminPageId, () => Promise<{ default: React.ComponentType }>> = {
  overview: () => import("./features/admin/dashboard/DashboardPage"),
  plan: () => import("./features/admin/plan/PlanPage"),
  stays: () => import("./features/admin/stays/StaysPage"),
  permanent: () => import("./features/admin/permanent/PermanentPage"),
  waiting: () => import("./features/admin/waiting/WaitingPage"),
  invoices: () => import("./features/admin/invoices/InvoicesPage"),
  settings: () => import("./features/admin/settings/SettingsPage"),
};

export const router = createBrowserRouter([
  { path: "/", element: <Navigate to="/admin" replace /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/login/reset", element: <ResetPage /> },
  // Sign-in links from before passwords were introduced.
  { path: "/login/verify", element: <Navigate to="/login" replace /> },
  {
    path: "/admin",
    element: <AdminLayout />,
    // Paths and roles come from access.ts; the guard runs before the page's chunk loads.
    children: ADMIN_PAGES.map((p) => {
      const element = <RequirePage page={p.id}>{lazyPage(ADMIN_LOADERS[p.id])}</RequirePage>;
      return p.segment ? { path: p.segment, element } : { index: true, element };
    }),
  },
  { path: "/dock/*", element: lazyPage(() => import("./features/dock/DockApp")) },
  { path: "*", element: <Navigate to="/admin" replace /> },
]);
